import { useState } from 'react';
import { User, Store, Phone, Mail, IndianRupee, Lock, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { sellerAPI, authAPI } from '@/lib/api';

export default function Profile({ profile, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
	  name: profile?.name || '',
	  contact_number: profile?.contact_number || '',
	  business_type: profile?.business_type || '',
	  business_name: profile?.business_name || '',
	  upi_id: profile?.upi_id || '',
	  delivery_amount: profile?.delivery_amount ?? 0,
	  free_delivery_start_range: profile?.free_delivery_start_range ?? 0
	});

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

	const handleSave = async () => {
	  setSaving(true);
	  try {
		const payload = {
		  ...formData,
		  delivery_amount: Number(formData.delivery_amount || 0),
		  free_delivery_start_range: Number(formData.free_delivery_start_range || 0),
		};

		await sellerAPI.updateProfile(payload);

		toast.success('Profile updated');
		setEditing(false);
		onUpdate?.();
	  } catch (error) {
		toast.error('Failed to update profile');
	  } finally {
		setSaving(false);
	  }
	};


  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <div className="space-y-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold">Profile</h1>
        <Card className="animate-pulse">
          <CardContent className="p-6 space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-muted rounded" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Profile</h1>
          <p className="text-muted-foreground mt-1">Manage your account settings</p>
        </div>
        {!editing ? (
          <Button onClick={() => setEditing(true)} data-testid="edit-profile-btn">
            Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-profile-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Info */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              {editing ? (
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="profile-name-input"
                />
              ) : (
                <p className="text-lg">{profile.name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Contact Number</Label>
              {editing ? (
                <Input
                  value={formData.contact_number}
                  onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                  data-testid="profile-phone-input"
                />
              ) : (
                <p className="text-lg flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  {profile.contact_number}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <p className="text-lg flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                {profile.email}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Business Info */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Store className="h-5 w-5 text-secondary" />
              Business Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Business Name</Label>
              {editing ? (
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  data-testid="profile-business-name-input"
                />
              ) : (
                <p className="text-lg">{profile.business_name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Business Type</Label>
              {editing ? (
                <Input
                  value={formData.business_type}
                  onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                  data-testid="profile-business-type-input"
                />
              ) : (
                <p className="text-lg">{profile.business_type}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>UPI ID</Label>
              {editing ? (
                <Input
                  value={formData.upi_id}
                  onChange={(e) => setFormData({ ...formData, upi_id: e.target.value })}
                  data-testid="profile-upi-input"
                />
              ) : (
                <p className="text-lg flex items-center gap-2">
                  <IndianRupee className="h-4 w-4 text-muted-foreground" />
                  {profile.upi_id}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

		{/* Delivery Settings */}
		<Card className="border-border/50">
		  <CardHeader>
			<CardTitle className="font-heading flex items-center gap-2">
			  <Truck className="h-5 w-5 text-primary" />
			  Delivery Settings
			</CardTitle>
		  </CardHeader>

		  <CardContent className="space-y-4">
			<div className="space-y-2">
			  <Label>Delivery Charge (₹)</Label>
			  {editing ? (
				<Input
				  type="number"
				  min="0"
				  step="1"
				  value={formData.delivery_amount}
				  onChange={(e) => setFormData({ ...formData, delivery_amount: e.target.value })}
				  data-testid="profile-delivery-amount-input"
				/>
			  ) : (
				<p className="text-lg flex items-center gap-2">
				  <IndianRupee className="h-4 w-4 text-muted-foreground" />
				  {Number(profile.delivery_amount ?? 0)}
				</p>
			  )}
			  <p className="text-xs text-muted-foreground">
				Order total free delivery range se kam ho to ye charge add hoga.
			  </p>
			</div>

			<div className="space-y-2">
			  <Label>Free Delivery From (₹)</Label>
			  {editing ? (
				<Input
				  type="number"
				  min="0"
				  step="1"
				  value={formData.free_delivery_start_range}
				  onChange={(e) => setFormData({ ...formData, free_delivery_start_range: e.target.value })}
				  data-testid="profile-free-delivery-range-input"
				/>
			  ) : (
				<p className="text-lg flex items-center gap-2">
				  <IndianRupee className="h-4 w-4 text-muted-foreground" />
				  {Number(profile.free_delivery_start_range ?? 0)}
				</p>
			  )}
			  <p className="text-xs text-muted-foreground">
				Agar order total ≥ is amount, delivery charge ₹0 ho jayega.
			  </p>
			</div>
		  </CardContent>
		</Card>



        {/* Shop Link */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="font-heading">Your Shop Link</CardTitle>
            <CardDescription>Share this link with your customers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-muted rounded-lg p-4">
              <p className="text-lg font-mono text-primary break-all">
                {window.location.origin}/shop/{profile.shop_slug}
              </p>
            </div>
            <Button 
              variant="outline" 
              className="mt-4 w-full"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/shop/${profile.shop_slug}`);
                toast.success('Link copied to clipboard');
              }}
            >
              Copy Link
            </Button>
          </CardContent>
        </Card>

        {/* Security */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Security
            </CardTitle>
            <CardDescription>Manage your password</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              onClick={() => setPasswordDialogOpen(true)}
              data-testid="change-password-btn"
            >
              Change Password
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current">Current Password</Label>
              <Input
                id="current"
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new">New Password</Label>
              <Input
                id="new"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm New Password</Label>
              <Input
                id="confirm"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePasswordChange} disabled={saving} data-testid="submit-password-btn">
              {saving ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
